import type { ComponentType, SVGProps } from 'react';
import {
    AccountIcon,
    BagIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ContactIcon,
    DeliveryIcon,
    EventsIcon,
    InfoIcon,
    InsideSpaceIcon,
    ListIcon,
    OffersIcon,
    StoresIcon,
} from '@/shared-ui/icons';

export const MENU_ITEM_ICON_NONE = 'none' as const;

/** Page Designer enum values for optional left/right icons on menu items. */
export const MENU_ITEM_ICON_VALUES = [
    MENU_ITEM_ICON_NONE,
    'chevronLeft',
    'chevronRight',
    'contact',
    'delivery',
    'stores',
    'events',
    'account',
    'info',
    'bag',
    'list',
    'offers',
    'insideSpace',
] as const;

export type MenuItemIconId = (typeof MENU_ITEM_ICON_VALUES)[number];

/** Invalid value emitted when cartridge codegen stringified the constant name instead of `"none"`. */
const LEGACY_MENU_ITEM_ICON_NONE = 'MENU_ITEM_ICON_NONE';

export function normalizeMenuItemIconId(icon?: string): string | undefined {
    if (!icon || icon === MENU_ITEM_ICON_NONE || icon === LEGACY_MENU_ITEM_ICON_NONE) {
        return undefined;
    }

    return icon;
}

const MENU_ITEM_ICON_MAP: Record<
    Exclude<MenuItemIconId, typeof MENU_ITEM_ICON_NONE | 'chevronLeft' | 'chevronRight'>,
    ComponentType<SVGProps<SVGSVGElement>>
> = {
    contact: ContactIcon,
    delivery: DeliveryIcon,
    stores: StoresIcon,
    events: EventsIcon,
    account: AccountIcon,
    info: InfoIcon,
    bag: BagIcon,
    list: ListIcon,
    offers: OffersIcon,
    insideSpace: InsideSpaceIcon,
};

const MENU_ITEM_CHEVRON_ICON_MAP: Record<'chevronLeft' | 'chevronRight', ComponentType<SVGProps<SVGSVGElement>>> = {
    chevronLeft: ChevronLeftIcon,
    chevronRight: ChevronRightIcon,
};

export function resolveMenuItemIcon(icon?: string): ComponentType<SVGProps<SVGSVGElement>> | undefined {
    const normalized = normalizeMenuItemIconId(icon);
    if (!normalized) {
        return undefined;
    }

    if (normalized === 'chevronLeft' || normalized === 'chevronRight') {
        return MENU_ITEM_CHEVRON_ICON_MAP[normalized];
    }

    return MENU_ITEM_ICON_MAP[normalized as keyof typeof MENU_ITEM_ICON_MAP];
}

export function isMenuItemIconId(value: string): value is MenuItemIconId {
    return (MENU_ITEM_ICON_VALUES as readonly string[]).includes(value);
}

/**
 * Reads a configured side icon from Page Designer data, falling back to legacy boolean chevron attributes.
 */
export function parseMenuItemSideIcon(
    data: Record<string, unknown>,
    side: 'left' | 'right'
): MenuItemIconId | undefined {
    const iconKey = side === 'left' ? 'leftIcon' : 'rightIcon';
    const legacyChevronKey = side === 'left' ? 'leftChevron' : 'rightChevron';
    const legacyIconId = side === 'left' ? 'chevronLeft' : 'chevronRight';

    const iconValue = normalizeMenuItemIconId(typeof data[iconKey] === 'string' ? data[iconKey] : undefined);
    if (iconValue && isMenuItemIconId(iconValue) && resolveMenuItemIcon(iconValue)) {
        return iconValue;
    }

    if (data[legacyChevronKey] === true) {
        return legacyIconId;
    }

    return undefined;
}
